import { useRef, useState } from "react";

import { Alert, Button, Checkbox, Modal, Progress, Typography } from "antd";
import { useNavigate } from "react-router-dom";

import { ErrorApi } from "../../lib/api/client";
import {
  crearVacia,
  esperarAnalisis,
  EXTENSIONES,
  subirArchivo,
  validarArchivo,
} from "../../lib/api/subida";

import "./subida.css";

const { Title, Paragraph, Text } = Typography;

type Fase =
  | { t: "inicio" }
  | { t: "subiendo"; porcentaje: number }
  | { t: "analizando" }
  | { t: "creando" };

/**
 * El primer estado del producto: subir la hoja de vida, o empezar en blanco.
 *
 * Al terminar NO aparece un modal de "completado, ¿continuar?" como en el
 * builder viejo. Se entra directo a la revisión: preguntarle a alguien si
 * quiere ver lo que acaba de pedir es un clic que no decide nada.
 */
export const SubidaPage = () => {
  const navegar = useNavigate();
  const entrada = useRef<HTMLInputElement>(null);

  const [fase, setFase] = useState<Fase>({ t: "inicio" });
  const [error, setError] = useState<string | null>(null);
  const [terminos, setTerminos] = useState(false);
  const [pidiendo, setPidiendo] = useState<"subir" | "vacia" | null>(null);

  const ocupado = fase.t !== "inicio";

  const fallar = (e: unknown) => {
    setError(
      e instanceof ErrorApi
        ? e.message
        : "Algo salió mal. Vuelve a intentarlo en un momento.",
    );
    setFase({ t: "inicio" });
  };

  const procesar = async (archivo: File) => {
    const problema = validarArchivo(archivo);
    if (problema) {
      setError(problema);
      return;
    }

    setError(null);
    setFase({ t: "subiendo", porcentaje: 0 });

    try {
      const { sessionId } = await subirArchivo(archivo, (p) =>
        setFase({ t: "subiendo", porcentaje: p }),
      );
      setFase({ t: "analizando" });
      const cvId = await esperarAnalisis(sessionId);
      navegar(`/editor/${cvId}`);
    } catch (e) {
      fallar(e);
    }
  };

  const empezarVacia = async () => {
    setError(null);
    setFase({ t: "creando" });
    try {
      navegar(`/editor/${await crearVacia()}`);
    } catch (e) {
      fallar(e);
    }
  };

  const confirmar = () => {
    const accion = pidiendo;
    setPidiendo(null);
    setTerminos(false);
    if (accion === "subir") entrada.current?.click();
    else if (accion === "vacia") void empezarVacia();
  };

  return (
    <main className="sub">
      <img className="sub-logo" src="/logo-almia.svg" alt="Almia" />

      <Title level={1} className="sub-titulo">
        ¿Aplicas a ofertas <span>y no te llaman?</span>
      </Title>

      <Paragraph className="sub-bajada">
        Sube tu hoja de vida y la revisamos contigo: te decimos qué mejorar y por
        qué, tú decides qué cambiar.
      </Paragraph>

      {error && (
        <Alert type="error" showIcon message={error} className="sub-alerta" closable
          onClose={() => setError(null)} />
      )}

      {ocupado ? (
        <div className="sub-progreso">
          {fase.t === "subiendo" && (
            <>
              <Text strong>Subiendo tu archivo…</Text>
              <Progress percent={fase.porcentaje} strokeColor="var(--marca-morado)" />
            </>
          )}
          {fase.t === "analizando" && (
            <>
              <Text strong>Leyendo tu hoja de vida…</Text>
              <Paragraph type="secondary" style={{ margin: "4px 0 10px" }}>
                Estamos extrayendo tu experiencia. Suele tardar menos de un minuto.
              </Paragraph>
              <Progress percent={100} status="active" showInfo={false}
                strokeColor="var(--marca-morado)" />
            </>
          )}
          {fase.t === "creando" && <Text strong>Preparando tu hoja de vida…</Text>}
        </div>
      ) : (
        <div className="sub-acciones">
          <Button type="primary" size="large" onClick={() => setPidiendo("subir")}>
            Ya tengo hoja de vida
          </Button>
          <Button size="large" onClick={() => setPidiendo("vacia")}>
            Empezar desde cero
          </Button>
        </div>
      )}

      <Paragraph type="secondary" className="sub-pie">
        PDF o Word, máximo 5 MB.
      </Paragraph>

      {/* El input vive fuera del flujo visual: el botón es lo que se ve, pero
          quien navega con teclado necesita llegar a un input de verdad. */}
      <input
        ref={entrada}
        type="file"
        accept={EXTENSIONES.join(",")}
        className="sub-input"
        onChange={(e) => {
          const archivo = e.target.files?.[0];
          e.target.value = "";
          if (archivo) void procesar(archivo);
        }}
      />

      <Modal
        open={pidiendo !== null}
        title="Autorización para el tratamiento de datos"
        onCancel={() => {
          setPidiendo(null);
          setTerminos(false);
        }}
        footer={[
          <Button key="no" onClick={() => setPidiendo(null)}>
            Cancelar
          </Button>,
          <Button key="si" type="primary" disabled={!terminos} onClick={confirmar}>
            Autorizo y continúo
          </Button>,
        ]}
      >
        <Paragraph>
          Autorizo a ALMIA CONSULTING S.A.S., NIT 901751970-1, el tratamiento de
          mis datos personales conforme a su{" "}
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
            Política de Tratamiento de Datos
          </a>
          , en cumplimiento de la Ley 1581 de 2012.
        </Paragraph>
        {/* La casilla empieza vacía y el botón deshabilitado: un consentimiento
            premarcado no es consentimiento. */}
        <Checkbox checked={terminos} onChange={(e) => setTerminos(e.target.checked)}>
          He leído y autorizo el tratamiento de mis datos.
        </Checkbox>
      </Modal>
    </main>
  );
};
